import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { UpdateModulePayload } from '@components/Admin/modules/module.types';
import { Cancel, ChevronRight, Description, OpenInNew, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { AppHeader, LoadingState } from '../components';
import { BackButton } from '../components/back-button';
import {
  useGetModuleDetailsQuery,
  useGetProviderInfoQuery,
  useGetTemplateDetailsQuery,
  useUpdateModuleMutation,
} from '../store/api';

const MODEL_NAMES: Record<string, string> = {
  'gpt-5-mini': 'GPT-5 Mini',
  'gpt-5-nano': 'GPT-5 Nano',
  'gpt-5': 'GPT-5',
  'gpt-4o': 'GPT-4o',
  'claude-3-5-sonnet': 'Claude 3.5 Sonnet',
  'gpt-4': 'GPT-4',
  'gpt-3.5-turbo': 'GPT-3.5-Turbo',
  'gpt-4-turbo': 'GPT-4-Turbo (OpenAI)',
  'azure-gpt-4-turbo': 'GPT-4-Turbo (Azure)',
  'claude-3-opus': 'Claude 3 Opus',
  'claude-3-sonnet': 'Claude 3 Sonnet',
};

export default function ModuleEditPage() {
  const { providerId, templateId, templateVersion } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get module info from query params
  const moduleId = searchParams.get('moduleId');
  const moduleType = searchParams.get('moduleType') as 'public' | 'user' | null;

  // Fetch data
  const { data: provider } = useGetProviderInfoQuery(
    { providerId: providerId || '' },
    { skip: !providerId },
  );

  const { data: templateData } = useGetTemplateDetailsQuery(
    {
      templateId: templateId || '',
      templateVersion: parseInt(templateVersion || '0', 10),
      type: (searchParams.get('templateType') as 'public' | 'user') || 'user',
    },
    { skip: !templateId || !templateVersion },
  );

  const { data: module, isLoading: moduleLoading } = useGetModuleDetailsQuery(
    {
      moduleId: moduleId || '',
      type: moduleType || 'public',
    },
    { skip: !moduleId || !moduleType },
  );

  const [updateModule, { isLoading: saving }] = useUpdateModuleMutation();

  // Form state
  const [moduleName, setModuleName] = useState('');
  const [generationPrompt, setGenerationPrompt] = useState('');
  const [semanticPrompt, setSemanticPrompt] = useState('');
  const [keywordPrompt, setKeywordPrompt] = useState('');
  const [updatePrompt, setUpdatePrompt] = useState('');
  const [selectedModels, setSelectedModels] = useState<string[]>(['gpt-4o']);
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState<number | string>(2500);
  const [isMetaModule, setIsMetaModule] = useState(false);
  const [isMarkdown, setIsMarkdown] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [publicName, setPublicName] = useState('');
  const [publicDescription, setPublicDescription] = useState('');
  const [publicCategories, setPublicCategories] = useState<string[]>([]);
  const [publicExample, setPublicExample] = useState('');
  const [language, setLanguage] = useState('en');
  const [userModuleDescription, setUserModuleDescription] = useState('');
  const [userExample, setUserExample] = useState('');
  const [isDeactivated, setIsDeactivated] = useState(false);

  // Initialize form data when module loads
  useEffect(() => {
    if (module) {
      setModuleName(module.module_name || '');
      setGenerationPrompt(module.generation_prompt || '');
      setSemanticPrompt(module.extraction_prompts?.semantic_prompt || '');
      setKeywordPrompt(module.extraction_prompts?.keyword_prompt || '');
      setSelectedModels(module.models || ['gpt-4o']);
      setTemperature(module.temperature || 0.7);
      setMaxTokens(module.max_tokens || 2500);
      setIsMetaModule(module.meta_module || false);
      setIsMarkdown(module.markdown || true);

      if (module._moduleType === 'public') {
        setUpdatePrompt(module.update_prompt || '');
        setIsPublic(module.public || false);
        setPublicName(module.public_name || '');
        setPublicDescription(module.public_description || '');
        setPublicCategories(module.public_categories || []);
        setPublicExample(module.public_example || '');
      } else if (module._moduleType === 'user') {
        setLanguage(module.language || 'en');
        setUserModuleDescription(module.user_module_description || '');
        setUserExample(module.user_example || '');
        setIsDeactivated(module.deactivated || false);
      }
    }
  }, [module]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!moduleId || !moduleType) return;

    const updatePayload: UpdateModulePayload = {
      _moduleType: moduleType,
      module_name: moduleName.trim(),
      generation_prompt: generationPrompt.trim(),
      extraction_prompts: {
        semantic_prompt: semanticPrompt.trim(),
        keyword_prompt: keywordPrompt.trim(),
      },
      models: selectedModels,
      temperature,
      max_tokens: typeof maxTokens === 'string' ? parseInt(maxTokens, 10) : maxTokens,
      meta_module: isMetaModule,
      markdown: isMarkdown,
    };

    // Add type-specific fields
    if (moduleType === 'public') {
      updatePayload.update_prompt = updatePrompt.trim() || undefined;
      updatePayload.public = isPublic;
      updatePayload.public_name = publicName.trim() || undefined;
      updatePayload.public_description = publicDescription.trim() || undefined;
      updatePayload.public_categories = publicCategories.length > 0 ? publicCategories : undefined;
      updatePayload.public_example = publicExample.trim() || undefined;
    } else {
      updatePayload.language = language;
      updatePayload.user_module_description = userModuleDescription.trim() || undefined;
      updatePayload.user_example = userExample.trim() || undefined;
      updatePayload.deactivated = isDeactivated;
    }

    try {
      await updateModule({
        moduleId,
        moduleVersion: module?.module_version || 1,
        type: moduleType,
        data: updatePayload,
      }).unwrap();

      // Navigate back to template detail
      navigate(
        `/admin-v2/provider/${providerId}/templates/${templateId}/${templateVersion}?type=${searchParams.get('templateType')}`,
      );
    } catch (error) {
      console.error('Failed to update module:', error);
    }
  };

  // Handle model selection
  const handleModelsChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedModels(typeof value === 'string' ? value.split(',') : value);
  };

  // Handle category management
  const handleCategoryAdd = (value: string) => {
    if (value && !publicCategories.includes(value)) {
      setPublicCategories([...publicCategories, value]);
    }
  };

  const handleCategoryDelete = (category: string) => {
    setPublicCategories(publicCategories.filter(c => c !== category));
  };

  if (!moduleId || !moduleType) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Invalid module parameters</h2>
            <p className="text-gray-600 mt-2">Module ID and type are required.</p>
          </div>
        </main>
      </div>
    );
  }

  if (moduleLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading module details..." />
        </main>
      </div>
    );
  }

  const template = templateData?.data;

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton />
        </div>
        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link
                  to={`/admin-v2/provider/${providerId}`}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                >
                  {provider?.name || 'Provider'}
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link
                  to={`/admin-v2/provider/${providerId}/templates`}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                >
                  Templates
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link
                  to={`/admin-v2/provider/${providerId}/templates/${templateId}/${templateVersion}?type=${searchParams.get('templateType')}`}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                >
                  {template?._templateType === 'public'
                    ? template.public_name || template.template_name
                    : template?.template_name || 'Template'}
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">Edit Module</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Modern Module Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Description className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">Edit Module</h1>
                  <p className="text-gray-600">{module?.module_name || 'Module'}</p>
                  <p className="text-sm text-gray-500">
                    ID: {moduleId} | Version: {module?.module_version || '?'} | Type: {moduleType}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Module Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card className="mb-6">
            <CardContent className="flex flex-col gap-4">
              <Typography variant="h6" className="mb-4">
                Basic Information
              </Typography>
              <div className="space-y-4">
                <TextField
                  fullWidth
                  required
                  label="Module Name"
                  value={moduleName}
                  onChange={e => setModuleName(e.target.value)}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" component="label" sx={{ fontWeight: 500 }}>
                    Generation Prompt
                  </Typography>
                  {moduleId && (
                    <Link
                      to={`/admin-v2/module-generation-sandbox/${moduleId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                    >
                      <OpenInNew
                        fontSize="small"
                        sx={{ fontSize: '1rem', color: 'primary.main' }}
                      />
                    </Link>
                  )}
                </Box>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={6}
                  value={generationPrompt}
                  onChange={e => setGenerationPrompt(e.target.value)}
                  sx={{ mt: -1 }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextField
                    fullWidth
                    label="Extraction: Semantic Prompt (;-delimited)"
                    multiline
                    rows={3}
                    value={semanticPrompt}
                    onChange={e => setSemanticPrompt(e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Extraction: Keyword Prompt (;-delimited)"
                    multiline
                    rows={3}
                    value={keywordPrompt}
                    onChange={e => setKeywordPrompt(e.target.value)}
                  />
                </div>
                {moduleType === 'public' && (
                  <TextField
                    fullWidth
                    label="Update Prompt"
                    multiline
                    rows={3}
                    value={updatePrompt}
                    onChange={e => setUpdatePrompt(e.target.value)}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Model Configuration */}
          <Card className="mb-6">
            <CardContent className="flex flex-col gap-4">
              <Typography variant="h6" className="mb-4">
                Model Configuration
              </Typography>
              <div className="space-y-4">
                <FormControl fullWidth>
                  <InputLabel>Models</InputLabel>
                  <Select
                    multiple
                    value={selectedModels}
                    onChange={handleModelsChange}
                    input={<OutlinedInput label="Models" />}
                    renderValue={selected => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(value => (
                          <Chip key={value} label={MODEL_NAMES[value] || value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {Object.entries(MODEL_NAMES).map(([code, name]) => (
                      <MenuItem key={code} value={code}>
                        <Checkbox checked={selectedModels.indexOf(code) > -1} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Typography gutterBottom>Temperature: {temperature}</Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, newValue) => setTemperature(newValue as number)}
                      step={0.1}
                      marks
                      min={0}
                      max={1}
                      valueLabelDisplay="auto"
                    />
                  </div>
                  <TextField
                    fullWidth
                    label="Max Tokens"
                    type="number"
                    InputProps={{ inputProps: { min: 0 } }}
                    value={maxTokens}
                    onChange={e => setMaxTokens(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Module Settings */}
          <Card className="mb-6">
            <CardContent className="flex flex-col gap-4">
              <Typography variant="h6" className="mb-4">
                Module Settings
              </Typography>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isMetaModule}
                        onChange={e => setIsMetaModule(e.target.checked)}
                      />
                    }
                    label="Meta Module"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isMarkdown}
                        onChange={e => setIsMarkdown(e.target.checked)}
                      />
                    }
                    label="Markdown"
                  />
                </div>

                {/* Public Module specific settings */}
                {moduleType === 'public' && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
                      }
                      label="Public"
                    />
                    <TextField
                      fullWidth
                      label="Public Display Name"
                      value={publicName}
                      onChange={e => setPublicName(e.target.value)}
                    />
                    <TextField
                      fullWidth
                      label="Public Description"
                      multiline
                      rows={2}
                      value={publicDescription}
                      onChange={e => setPublicDescription(e.target.value)}
                    />
                    <TextField
                      fullWidth
                      label="Public Example"
                      multiline
                      rows={3}
                      value={publicExample}
                      onChange={e => setPublicExample(e.target.value)}
                    />
                    <div>
                      <TextField
                        fullWidth
                        label="Public Categories (Press Enter to add)"
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const input = e.target as HTMLInputElement;
                            handleCategoryAdd(input.value);
                            input.value = '';
                          }
                        }}
                      />
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {publicCategories.map(category => (
                          <Chip
                            key={category}
                            label={category}
                            onDelete={() => handleCategoryDelete(category)}
                          />
                        ))}
                      </Box>
                    </div>
                  </>
                )}

                {/* User Module specific settings */}
                {moduleType === 'user' && (
                  <>
                    <FormControl fullWidth>
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={language}
                        label="Language"
                        onChange={e => setLanguage(e.target.value)}
                      >
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="fr">French</MenuItem>
                        <MenuItem value="es">Spanish</MenuItem>
                        <MenuItem value="de">German</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isDeactivated}
                          onChange={e => setIsDeactivated(e.target.checked)}
                        />
                      }
                      label="Deactivated"
                    />
                    <TextField
                      fullWidth
                      label="User Module Description"
                      multiline
                      rows={2}
                      value={userModuleDescription}
                      onChange={e => setUserModuleDescription(e.target.value)}
                    />
                    <TextField
                      fullWidth
                      label="User Example"
                      multiline
                      rows={3}
                      value={userExample}
                      onChange={e => setUserExample(e.target.value)}
                    />
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex justify-end gap-4">
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={() =>
                navigate(
                  `/admin-v2/provider/${providerId}/templates/${templateId}/${templateVersion}?type=${searchParams.get('templateType')}`,
                )
              }
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              disabled={saving}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
